/* @jsx h */
import { defineComponent, h, ref, computed, onMounted } from "vue";
import { LemmonModal } from "@lemmonhq/uikit";
import {
  uploadsAPI,
  type RestaurantAsset,
  type UploadsAPI,
} from "@app/data/api/uploads";
import { useAssetLibrary } from "./useAssetLibrary";
import { AssetDetails } from "../AssetDetails";

export { useAssetLibrary };

interface AssetPickerProps {
  /** Selected restaurant_assets.id, or null when nothing is chosen. */
  value: string | null;
  /** Called with the chosen asset, or null when the selection is cleared. */
  onSelect: (asset: RestaurantAsset | null) => void;
  /** Optional filter to one asset collection (restaurant_assets.type). */
  type?: string;
  /** Placeholder shown on the trigger when nothing is selected. */
  placeholder?: string;
  /** Trigger thumbnail shape. */
  aspect?: "square" | "wide";
  disabled?: boolean;
  uploadsApi?: UploadsAPI;
  /**
   * When set, offers "Upload" beside the picker: the file is uploaded into this
   * asset collection through the same AssetDetails flow as Settings → Assets,
   * then selected.
   */
  uploadType?: string;
}

/** Human filename from the stored object path. */
const filename = (a: RestaurantAsset) => a.directory.split("/").pop() ?? a.id;

/**
 * Visual image picker over the restaurant's uploaded assets. Presents a
 * thumbnail trigger; clicking opens a searchable gallery to choose (or clear)
 * an image. Reusable anywhere a `restaurant_assets` image is picked — it holds
 * only an asset id and hands the caller the full asset on select.
 */
export const AssetPicker = defineComponent<AssetPickerProps>({
  name: "AssetPicker",
  props: [
    "value",
    "onSelect",
    "type",
    "placeholder",
    "aspect",
    "disabled",
    "uploadsApi",
    "uploadType",
  ],
  setup(props) {
    const api = props.uploadsApi ?? uploadsAPI;
    const library = useAssetLibrary(api);
    const open = ref(false);
    const uploading = ref(false);
    const search = ref("");

    onMounted(() => library.load());

    const items = computed(() =>
      library.assets.value.filter((a) => !props.type || a.type === props.type),
    );
    const filtered = computed(() => {
      const q = search.value.trim().toLowerCase();
      if (!q) return items.value;
      return items.value.filter((a) => filename(a).toLowerCase().includes(q));
    });
    const selected = computed(() =>
      props.value
        ? (library.assets.value.find((a) => a.id === props.value) ?? null)
        : null,
    );

    const choose = (a: RestaurantAsset | null) => {
      props.onSelect(a);
      open.value = false;
    };

    /** A fresh upload joins every open picker's gallery and is selected. */
    const uploaded = async (asset: RestaurantAsset) => {
      uploading.value = false;
      await library.load(true);
      choose(asset);
    };

    return () => (
      <>
        <button
          type="button"
          class="group w-full overflow-hidden rounded-lg border border-base-content/15 bg-base-200/40 text-left transition hover:border-primary/50 disabled:opacity-50"
          disabled={props.disabled}
          onClick={() => (open.value = true)}
        >
          <div
            class={`flex w-full items-center justify-center bg-base-300 ${
              props.aspect === "wide" ? "aspect-[3/1]" : "aspect-square"
            }`}
          >
            {selected.value?.url ? (
              <img
                src={selected.value.url}
                alt={filename(selected.value)}
                class="h-full w-full object-cover"
              />
            ) : (
              <span class="material-symbols-outlined text-2xl text-base-content/30">
                add_photo_alternate
              </span>
            )}
          </div>
          <div class="flex items-center justify-between gap-2 px-2 py-1.5">
            <span class="truncate text-[11px] text-base-content/70">
              {selected.value
                ? filename(selected.value)
                : (props.placeholder ?? "Choose image")}
            </span>
            <span class="material-symbols-outlined text-sm text-base-content/40">
              edit
            </span>
          </div>
        </button>

        {props.uploadType && (
          <button
            type="button"
            class="btn btn-ghost btn-xs mt-1 w-full gap-1"
            disabled={props.disabled}
            onClick={() => (uploading.value = true)}
          >
            <span class="material-symbols-outlined text-sm">upload</span>
            Upload
          </button>
        )}

        {uploading.value && props.uploadType && (
          <AssetDetails
            mode="add"
            assetType={props.uploadType}
            isOpen={true}
            uploadsApi={api}
            onClose={() => (uploading.value = false)}
            onSave={uploaded}
          />
        )}

        {open.value && (
          <LemmonModal
            title="Choose image"
            height="72vh"
            onClose={() => (open.value = false)}
          >
            <div class="flex flex-col gap-3 p-1">
              <input
                class="input input-bordered input-sm w-full"
                placeholder="Search images…"
                value={search.value}
                onInput={(e) =>
                  (search.value = (e.target as HTMLInputElement).value)
                }
              />

              {library.loading.value ? (
                <div class="py-10 text-center text-sm text-base-content/50">
                  Loading images…
                </div>
              ) : library.error.value ? (
                <div class="alert alert-error text-sm">
                  {library.error.value.message}
                </div>
              ) : (
                <div class="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
                  <button
                    type="button"
                    class={`flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-base-content/20 text-base-content/50 transition hover:border-error/50 hover:text-error ${
                      !props.value ? "opacity-40" : ""
                    }`}
                    onClick={() => choose(null)}
                  >
                    <span class="material-symbols-outlined">block</span>
                    <span class="text-[11px]">None</span>
                  </button>

                  {filtered.value.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      class={`overflow-hidden rounded-lg border-2 transition ${
                        a.id === props.value
                          ? "border-primary"
                          : "border-transparent hover:border-base-content/30"
                      }`}
                      onClick={() => choose(a)}
                      title={filename(a)}
                    >
                      <div class="aspect-square w-full bg-base-200">
                        <img
                          src={a.url}
                          alt={filename(a)}
                          class="h-full w-full object-cover"
                        />
                      </div>
                    </button>
                  ))}

                  {filtered.value.length === 0 && (
                    <div class="col-span-full py-8 text-center text-sm text-base-content/50">
                      {search.value
                        ? "No images match your search."
                        : props.uploadType
                          ? "No images uploaded yet. Use Upload to add one."
                          : "No images uploaded yet. Add them under the restaurant's assets."}
                    </div>
                  )}
                </div>
              )}
            </div>
          </LemmonModal>
        )}
      </>
    );
  },
});
